import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import {
  useDeleteStudent,
  useStudentQuery,
  useUpdateStudent,
} from "../api/queries";
import { StudentForm } from "../ui/StudentForm";

export function StudentEditPage() {
  const nav = useNavigate({
    from: "/app/students/$id/edit",
  });
  const { id } = useParams({ from: "/app/students/$id/edit" });
  const { data, ...result } = useStudentQuery(id);
  const updateStudent = useUpdateStudent(id);
  const deleteStudent = useDeleteStudent();

  const student = data;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ນັກຮຽນ", to: "/app/students" },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂນັກຮຽນ
            </h2>
            <p className="text-muted-foreground">ປັບປຸງລາຍລະອຽດຂອງນັກຮຽນ.</p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={async () => {
                const ok = await import("@devhop/ui").then((m) =>
                  m.confirm({
                    title: "ລຶບນັກຮຽນ",
                    description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບນັກຮຽນນີ້?",
                    actionText: "ລຶບ",
                    ActionProps: {
                      variant: "destructive",
                    },
                  }),
                );
                if (!ok) return;

                try {
                  await toast.promise(deleteStudent.run(id), {
                    loading: "ກຳລັງລຶບ...",
                    success: () => {
                      nav({ to: "/app/students" });
                      return "ລຶບນັກຮຽນສຳເລັດ";
                    },
                    error: "ລຶບນັກຮຽນລົ້ມເຫຼວ",
                  });
                } catch (error) {
                  // Error handling is done by toast.promise
                }
              }}
            >
              ລຶບ
            </Button>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/students/$id", params: { id } })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດນັກຮຽນ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!student ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <StudentForm
                initialValues={{
                  memberId: student.memberId,
                  name: student.name,
                  nickname: student.nickname ?? "",
                  phoneNumber: student.phoneNumber || "",
                  email: student.email || "",
                  status: student.status,
                  notes: student.notes || "",
                  groupId: student.groupId || undefined,
                  dateOfBirth: student.dateOfBirth || undefined,
                  gender: student.gender || undefined,
                  whatsappOptIn: student.whatsappOptIn,
                }}
                onSubmit={async (vals) => {
                  try {
                    await updateStudent.mutateAsync(vals);
                    nav({ to: "/app/students/$id", params: { id } });
                  } catch (error) {
                    // Error handling is done by the mutation
                  }
                }}
                submitting={updateStudent.isPending}
              />
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
